import ConnectorService from '@/services/connector.service';
import { useDebugStore } from '@/stores/debug';

const SECTION_ORDER = {
  'My Current Courses': 0,
  'Add to Transcript': 1,
  'My Upcoming Courses': 2,
  'Transcripted Courses': 3,
  'Past Courses': 4
};

export function useDebugPanel() {
  const debugStore = useDebugStore();

  async function fetchEnrichedCourses() {
    debugStore.setLoading(true);
    debugStore.setError(null);

    try {
      const ld = ConnectorService.learnerData;

      if (!ld) {
        debugStore.setError('Not logged in — learnerData is null');
        return;
      }

      const learnerId = ld._learnerId || ld._learnerApplicationId || ld.learnerId || ld.learnerApplicationId;

      if (!learnerId) {
        debugStore.setError('No learnerId found on learnerData');
        return;
      }

      const apiPath = `/v2/api/learners/${learnerId}/courses?enrich=true&cardLayout=compact`;
      const response = await ConnectorService._doConnect(apiPath, 'get', null, { skipLoading: true });

      if (response?.data?.courses) {
        const courses = response.data.courses
          .filter((c) => c.ui?.cardLayout === 'compact')
          .sort((a, b) => {
            const oa = SECTION_ORDER[a.section?.name] ?? 5;
            const ob = SECTION_ORDER[b.section?.name] ?? 5;
            return oa - ob;
          });
        debugStore.setEnrichedCourses(courses);

        if (courses.length === 0) {
          debugStore.setError('API returned courses but none had cardLayout=compact');
        }
      } else {
        debugStore.setError('API returned no courses array');
      }
    } catch (error) {
      debugStore.setError(error.message || 'Request failed');
      console.error('Debug panel fetch error:', error);
    } finally {
      debugStore.setLoading(false);
    }
  }

  async function registerForCertificate() {
    const slug = debugStore.certificateSlug?.trim();
    if (!slug) return;

    debugStore.setRegistering(true);
    debugStore.setCertificateMessage(null);

    try {
      const ld = ConnectorService.learnerData;

      if (!ld) {
        debugStore.setCertificateMessage({ type: 'error', text: 'Not logged in — learnerData is null' });
        return;
      }

      const learnerId = ld._learnerId || ld._learnerApplicationId || ld.learnerId || ld.learnerApplicationId;

      if (!learnerId) {
        debugStore.setCertificateMessage({ type: 'error', text: 'No learnerId found on learnerData' });
        return;
      }

      await ConnectorService.registerForCertificate({
        certificate: slug,
        ea_learner_application: learnerId
      });

      debugStore.setCertificateMessage({ type: 'success', text: `Registered for "${slug}"` });
    } catch (error) {
      let msg = error.response?.data?.message || error.message || 'Failed to register for certificate';

      if (msg.includes('already registered')) {
        msg = 'Already registered for this certificate';
      } else if (msg.includes('not found')) {
        msg = 'Certificate program not found';
      } else if (msg.includes('not active')) {
        msg = 'Certificate program is not available';
      }

      debugStore.setCertificateMessage({ type: 'error', text: msg });
      console.error('Debug panel certificate registration error:', error);
    } finally {
      debugStore.setRegistering(false);
    }
  }

  return { fetchEnrichedCourses, registerForCertificate };
}
